/**
 * Collection Load More - INFINITE SCROLL with AUTO REORDER
 * Version 6.1 - Fixed hasActiveFilters to ignore auto-instock
 * - Detects when Shopify filter replaces grid
 * - Auto reconnects observer to new grid
 * - Always reorders sold out products to bottom
 * - Background loads all products UNLESS sidebar filters active
 */

(function() {
  'use strict';

  const CONFIG = {
    selectors: {
      grid: '[data-products-grid] .js-grid',
      loadMoreButton: '[data-load-more-button]',
      loadMoreContainer: '[data-load-more-container]',
      productItem: '.product-grid-item',
      soldBadge: '.product__badge--sold'
    },
    productsPerLoad: 48,
    scrollThreshold: 500,
    autoLoadEnabled: true,
    debug: true,
    reconnectDelay: 200,
    documentObserverDebounce: 300
  };

  let allProducts = [];
  let availableProducts = [];
  let soldOutProducts = [];
  let isBackgroundLoading = false;
  let allLoaded = false;
  let isLoadingMore = false;
  let scrollHandler = null;
  let gridObserver = null;
  let documentObserver = null;
  let isReordering = false;
  let observerPaused = false;
  let documentObserverTimeout = null;

  function isSoldOut(productWrapper) {
    const soldBadge = productWrapper.querySelector(CONFIG.selectors.soldBadge);
    return soldBadge !== null;
  }

  function getProductsFromGrid(gridElement) {
    if (!gridElement) return [];
    
    return Array.from(gridElement.children).filter(child => {
      return child.querySelector(CONFIG.selectors.productItem);
    });
  }

  function hasActiveFilters() {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    
    for (let [key, value] of params) {
      // UPDATED: Only check for sidebar filters (filter.v.option)
      // IGNORE auto-instock filter (filter.v.availability)
      if (key.startsWith('filter.v.option')) {
        if (CONFIG.debug) {
          console.log('🎛️ Sidebar filter detected:', key, '=', value);
        }
        return true;
      }
    }
    
    return false;
  }

  function reorderCurrentProducts() {
    if (isReordering || observerPaused) return;
    
    isReordering = true;
    observerPaused = true;

    const grid = document.querySelector(CONFIG.selectors.grid);
    if (!grid) {
      isReordering = false;
      observerPaused = false;
      return;
    }

    const currentProducts = getProductsFromGrid(grid);
    
    if (currentProducts.length === 0) {
      isReordering = false;
      observerPaused = false;
      return;
    }

    const available = [];
    const soldOut = [];

    currentProducts.forEach(product => {
      if (isSoldOut(product)) {
        soldOut.push(product);
      } else {
        available.push(product);
      }
    });

    if (soldOut.length === 0) {
      if (CONFIG.debug) {
        console.log('✅ No sold out products');
      }
      isReordering = false;
      observerPaused = false;
      return;
    }

    if (CONFIG.debug) {
      console.log(`🔄 Reordering: ${available.length} available, ${soldOut.length} sold out`);
    }

    // Sengaja TIDAK memakai grid.innerHTML = '' di sini.
    //
    // appendChild pada node yang sudah menempel di DOM itu MEMINDAHKAN node,
    // bukan menyalinnya, jadi dua loop di bawah sudah menghasilkan urutan akhir
    // yang persis sama tanpa perlu mengosongkan grid lebih dulu.
    //
    // Mengosongkannya justru merusak: untuk sesaat grid jadi nol tinggi,
    // dokumen menyusut dari sekitar 20.600 px ke beberapa ratus piksel, dan
    // browser memaksa posisi scroll turun ke batas baru. Begitu 125 kartu
    // dipasang lagi tingginya kembali, tapi posisi scroll pemakai sudah telanjur
    // hilang. Itulah halaman yang "melompat ke atas dulu" beberapa detik sesudah
    // dibuka, dan karena fungsi ini dipicu berkali kali lewat setTimeout dan
    // MutationObserver, lompatannya terjadi lebih dari sekali.
    //
    // Aman karena getProductsFromGrid mengembalikan SEMUA anak grid yang berisi
    // .product-grid-item, dan di grid 247 setiap anak memang kartu produk.
    // available + soldOut sudah mencakup seluruh anak, tidak ada yang tertinggal.
    available.forEach(product => {
      grid.appendChild(product);
    });

    soldOut.forEach(product => {
      grid.appendChild(product);
    });

    if (CONFIG.debug) {
      console.log('✅ Reorder complete');
    }

    setTimeout(() => {
      isReordering = false;
      observerPaused = false;
    }, 200);
  }

  function setupGridObserver() {
    const grid = document.querySelector(CONFIG.selectors.grid);
    
    if (!grid) return;

    if (gridObserver) {
      gridObserver.disconnect();
      if (CONFIG.debug) {
        console.log('🔌 Disconnected old grid observer');
      }
    }

    let mutationTimeout;

    gridObserver = new MutationObserver((mutations) => {
      if (observerPaused || isReordering) {
        return;
      }

      clearTimeout(mutationTimeout);
      mutationTimeout = setTimeout(() => {
        if (CONFIG.debug) {
          console.log('📡 Grid changed - triggering reorder');
        }
        reorderCurrentProducts();
      }, 300);
    });

    gridObserver.observe(grid, {
      childList: true,
      subtree: false
    });

    if (CONFIG.debug) {
      console.log('👁️ Grid observer active');
    }
  }

  function setupDocumentObserver() {
    if (documentObserver) {
      documentObserver.disconnect();
    }

    documentObserver = new MutationObserver((mutations) => {
      clearTimeout(documentObserverTimeout);
      
      documentObserverTimeout = setTimeout(() => {
        const grid = document.querySelector(CONFIG.selectors.grid);
        
        if (!grid) return;

        const hasGridMutation = mutations.some(mutation => {
          if (mutation.target.matches && mutation.target.matches('[data-products-grid]')) {
            return true;
          }
          return Array.from(mutation.addedNodes).some(node => {
            if (node.nodeType === 1) {
              return node.querySelector && node.querySelector(CONFIG.selectors.grid);
            }
            return false;
          });
        });

        if (hasGridMutation) {
          if (CONFIG.debug) {
            console.log('📡 Document observer: Grid replaced by filter');
          }
          
          setTimeout(() => {
            reconnectObserver();
          }, CONFIG.reconnectDelay);
        }
      }, CONFIG.documentObserverDebounce);
    });

    const productsContainer = document.querySelector('[data-products-grid]');
    if (productsContainer) {
      documentObserver.observe(productsContainer, {
        childList: true,
        subtree: true
      });

      if (CONFIG.debug) {
        console.log('🌐 Document observer active - watching for grid replacement');
      }
    }
  }

  function reconnectObserver() {
    if (CONFIG.debug) {
      console.log('🔄 Reconnecting observer to new grid...');
    }

    setupGridObserver();

    setTimeout(() => {
      reorderCurrentProducts();
    }, 100);
  }

  async function loadAllProductsInBackground() {
    // UPDATED: Check for SIDEBAR filters only (not auto-instock)
    if (hasActiveFilters()) {
      if (CONFIG.debug) {
        console.log('⚠️ Sidebar filters active - skip background load');
        console.log('⚠️ Shopify pagination will handle filtered results');
      }
      allLoaded = true;
      return;
    }

    if (isBackgroundLoading || allLoaded) return;
    
    isBackgroundLoading = true;

    if (CONFIG.debug) {
      console.log('⚡ Background loading all products...');
    }

    const grid = document.querySelector(CONFIG.selectors.grid);
    if (!grid) return;

    const initialProducts = getProductsFromGrid(grid);
    allProducts = [...initialProducts];

    const button = document.querySelector(CONFIG.selectors.loadMoreButton);
    if (!button) {
      if (CONFIG.debug) {
        console.log('✅ Single page - all products already loaded');
      }
      separateAndPrepare();
      return;
    }

    const totalPages = parseInt(button.getAttribute('data-total-pages')) || 1;
    const currentPage = parseInt(button.getAttribute('data-current-page')) || 1;

    if (totalPages <= currentPage) {
      separateAndPrepare();
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const pagesToLoad = [];
    
    for (let page = currentPage + 1; page <= totalPages; page++) {
      urlParams.set('page', page);
      const pageUrl = window.location.pathname + '?' + urlParams.toString();
      pagesToLoad.push({ page, url: pageUrl });
    }

    if (CONFIG.debug) {
      console.log(`⚡ Loading ${pagesToLoad.length} additional pages...`);
    }

    const startTime = Date.now();
    
    try {
      const loadPromises = pagesToLoad.map(({ page, url }) => 
        fetch(url)
          .then(response => response.text())
          .then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const nextGrid = doc.querySelector(CONFIG.selectors.grid);
            
            if (nextGrid) {
              const products = getProductsFromGrid(nextGrid);
              if (CONFIG.debug) {
                console.log(`✅ Page ${page}: ${products.length} products loaded`);
              }
              return products;
            }
            return [];
          })
          .catch(error => {
            console.error(`❌ Page ${page} failed:`, error);
            return [];
          })
      );

      const results = await Promise.all(loadPromises);
      
      results.forEach(products => {
        allProducts.push(...products);
      });

      const loadTime = ((Date.now() - startTime) / 1000).toFixed(2);
      
      if (CONFIG.debug) {
        console.log(`⚡ Background load complete: ${allProducts.length} total products in ${loadTime}s`);
      }

    } catch (error) {
      console.error('Error loading pages:', error);
    }

    allLoaded = true;
    separateAndPrepare();
  }

  function separateAndPrepare() {
    availableProducts = [];
    soldOutProducts = [];

    allProducts.forEach(product => {
      if (isSoldOut(product)) {
        soldOutProducts.push(product);
      } else {
        availableProducts.push(product);
      }
    });

    if (CONFIG.debug) {
      console.log(`📊 Separated: ${availableProducts.length} available, ${soldOutProducts.length} sold out`);
    }

    checkLoadMoreButton();
  }

  function checkLoadMoreButton() {
    const button = document.querySelector(CONFIG.selectors.loadMoreButton);
    const container = document.querySelector(CONFIG.selectors.loadMoreContainer);
    
    if (!button || !container || !allLoaded) return;

    const grid = document.querySelector(CONFIG.selectors.grid);
    if (!grid) return;

    const currentProducts = getProductsFromGrid(grid);
    const totalProducts = availableProducts.length + soldOutProducts.length;
    const displayedCount = currentProducts.length;

    if (displayedCount < totalProducts) {
      const remaining = totalProducts - displayedCount;
      const nextLoad = Math.min(remaining, CONFIG.productsPerLoad);
      
      container.innerHTML = `
        <div class="collection__load-more-info" style="text-align:center;padding:20px;">
          <button 
            class="button button--secondary collection__load-more-button" 
            data-load-more-trigger
            style="margin-bottom:10px;">
            Load ${nextLoad} More Products
          </button>
          <p style="font-size:14px;opacity:0.7;margin:0;">
            Showing ${displayedCount} of ${totalProducts} products
            <br><span style="font-size:12px;">Or scroll down to auto-load</span>
          </p>
        </div>
      `;
      
      const btn = container.querySelector('[data-load-more-trigger]');
      if (btn) {
        btn.addEventListener('click', handleLoadMoreClick);
      }
    } else {
      container.innerHTML = '';
    }
  }

  function handleLoadMoreClick() {
    const grid = document.querySelector(CONFIG.selectors.grid);
    if (!grid) return;

    const currentProducts = getProductsFromGrid(grid);
    const displayedCount = currentProducts.length;
    const orderedProducts = [...availableProducts, ...soldOutProducts];
    
    const endIndex = displayedCount + CONFIG.productsPerLoad;
    const productsToShow = orderedProducts.slice(displayedCount, endIndex);

    productsToShow.forEach(product => {
      grid.appendChild(product);
    });

    if (CONFIG.debug) {
      console.log(`✅ Loaded ${productsToShow.length} more products`);
    }
  }

  function setupInfiniteScroll() {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
    }

    let ticking = false;

    function checkScrollPosition() {
      const container = document.querySelector(CONFIG.selectors.loadMoreContainer);
      if (!container) {
        ticking = false;
        return;
      }

      const button = container.querySelector('[data-load-more-trigger]');
      if (!button) {
        ticking = false;
        return;
      }

      const rect = button.getBoundingClientRect();
      const isNearViewport = rect.top < (window.innerHeight + CONFIG.scrollThreshold);

      if (isNearViewport && !isLoadingMore && allLoaded) {
        if (CONFIG.debug) {
          console.log('📜 Infinite scroll triggered');
        }
        isLoadingMore = true;
        handleLoadMoreClick();
        
        setTimeout(() => {
          isLoadingMore = false;
        }, 500);
      }

      ticking = false;
    }

    function onScroll() {
      if (!CONFIG.autoLoadEnabled) return;
      
      if (!ticking) {
        window.requestAnimationFrame(checkScrollPosition);
        ticking = true;
      }
    }

    scrollHandler = onScroll;
    window.addEventListener('scroll', scrollHandler, { passive: true });

    if (CONFIG.debug) {
      console.log('✅ Infinite scroll enabled');
    }
  }

  function init() {
    if (CONFIG.debug) {
      console.log('🚀 Collection v6.1 - Fixed hasActiveFilters');
      console.log('🚀 Will background load UNLESS sidebar filters (filter.v.option) active');
    }

    setupGridObserver();
    setupDocumentObserver();

    document.addEventListener('categoryFilterLoaded', () => {
      if (CONFIG.debug) {
        console.log('🎯 Pills filter detected - reconnecting observer');
      }
      
      setTimeout(() => {
        reconnectObserver();
      }, CONFIG.reconnectDelay);
    });

    setTimeout(() => {
      reorderCurrentProducts();
    }, 500);

    loadAllProductsInBackground();
    
    setTimeout(() => {
      setupInfiniteScroll();
    }, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.collectionLoadMore = {
    status: function() {
      const grid = document.querySelector(CONFIG.selectors.grid);
      const currentProducts = grid ? getProductsFromGrid(grid) : [];
      
      console.log('=== STATUS ===');
      console.log('Current grid:', currentProducts.length);
      console.log('Background total:', allProducts.length);
      console.log('Has sidebar filters?', hasActiveFilters());
      console.log('Observer paused?', observerPaused);
      console.log('Grid observer active?', gridObserver !== null);
      console.log('Document observer active?', documentObserver !== null);
    },
    reorder: reorderCurrentProducts,
    reconnect: reconnectObserver,
    config: CONFIG
  };
})();